// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { onClose: () => void };

/**
 * Hangs under the chip that opened it. The person's own row goes to their page; below the divider are
 * the two things that belong to them rather than to the system — the language this app speaks, and
 * the way out of it.
 *
 * The language switch lives here because it is a preference about the reader, not a setting of the
 * product, and this is the only menu in the app that is about the reader.
 *
 * **Every control in here closes it, signing out included.** A menu that stays open after it has been
 * used leaves the reader to work out whether the press registered.
 *
 * Signing out has to close it *itself*. `PopupPaper` is mounted in the router's root layout, beside the
 * whole route tree rather than inside it, so dropping the token unmounts the dashboard and leaves this
 * menu floating over the sign-in screen.
 */
export default function ProfilePopup({ onClose }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();
    const admin = useAuth((state) => state.admin);
    const logout = useAuth((state) => state.logout);
    const lang = useLang((state) => state.lang) ?? "ar";
    const setLang = useLang((state) => state.setLang);

    const signOut = useFetch("/auth/logout", { method: "POST" }, false);

    /**
     * The token is revoked on the server **first**, then forgotten here.
     *
     * The order is forced rather than chosen: `useFetch` reads the token at render, so clearing it in
     * the same handler would send the revoke without an `Authorization` header and leave a live token
     * in `admin.tokens` for ever. So the popup stays open and the button waits.
     *
     * A failed revoke still signs out locally — a network that is down must not be able to trap
     * somebody in a session.
     *
     * The close comes first so the menu is already on its way out as the sign-in screen replaces the
     * dashboard behind it. It cannot move to the click handler: closing unmounts this component, and
     * the effect that signs out would never run.
     */
    useEffect(() => {
        if (!signOut.succeededAt && !signOut.error) return;
        onClose();
        logout();
    }, [signOut.succeededAt, signOut.error, onClose, logout]);

    return (
        <section className="popup-card profile-popup">
            <button
                type="button"
                className="profile-popup__identity"
                onClick={() => {
                    onClose();
                    navigate("/profile");
                }}
            >
                <Avatar name={admin?.name ?? ""} picture={admin?.picture} size={36} bordered />
                <span className="profile-popup__text">
                    <span className="profile-popup__name">{admin?.name}</span>
                    <span className="profile-popup__role">{admin?.isSuper ? t.admins.super : t.sideBar.kind}</span>
                </span>
            </button>

            <span className="profile-popup__divider" />

            {/* Each language in its own words, never translated — see `constants.languageNames`. */}
            <div className="profile-popup__langs">
                {constants.languages.map((item) => (
                    <button
                        key={item}
                        type="button"
                        className={`profile-popup__lang${item === lang ? " profile-popup__lang--on" : ""}`}
                        onClick={() => {
                            setLang(item);
                            onClose();
                        }}
                    >
                        {constants.languageNames[item]}
                    </button>
                ))}
            </div>

            <button
                type="button"
                className="profile-popup__action profile-popup__action--logout"
                onClick={() => signOut.invoke()}
                disabled={signOut.loading}
            >
                <Icon name="logout" size={18} />
                <span>{signOut.loading ? t.common.loading : t.common.logout}</span>
            </button>
        </section>
    );
}
